import React, { useState, useEffect, useRef } from 'react';
import { Check, Calendar, Users, Clock, Zap, TrendingUp, Crown } from 'lucide-react';

// Custom hook for scroll animation
const useScrollAnimation = (threshold = 0.1) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(element);
        }
      },
      { threshold, rootMargin: '0px 0px -50px 0px' }
    );

    observer.observe(element);
    return () => element && observer.unobserve(element);
  }, [threshold]);

  return [ref, isVisible];
};

const PricingSection = () => {
  const [headerRef, headerVisible] = useScrollAnimation(0.2);
  const [cardsRef, cardsVisible] = useScrollAnimation(0.1);

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const pricingOptions = [
    {
      name: "One-Off Lesson",
      price: "34.99",
      period: "per lesson",
      description: "Perfect for trying out our tuition or occasional support",
      icon: Zap,
      features: [
        '1 hour group session',
        'Higher or Foundation tier',
        'All exam boards covered',
        'Past paper practice',
        'Examiner insights included',
        'Small group (4-6 students)',
        'Flexible scheduling',
        'No commitment required'
      ],
      popular: false,
      buttonText: "Book Single Lesson"
    },
    {
      name: "Monthly Package",
      price: "119.99",
      period: "per month",
      description: "Best value! Regular weekly sessions for consistent progress",
      icon: TrendingUp,
      features: [
        '4 hours of tuition per month',
        '1 session per week',
        'Higher or Foundation tier',
        'All exam boards covered',
        'Past paper practice',
        'Examiner insights included',
        'Small group (4-6 students)',
        'Progress tracking',
        'Priority booking',
        'Save £20 per month!'
      ],
      popular: true,
      buttonText: "Book Monthly Package",
      savings: "Save £20"
    },
    {
      name: "VIP 1:1 Maths",
      price: "69.99",
      period: "per hour",
      description: "Premium one-on-one Maths tuition - exclusive focus on your child's needs",
      icon: Crown,
      features: [
        '1 hour private session',
        'Exclusive 1-on-1 attention',
        'GCSE/IGCSE Maths only',
        'Fully personalized learning',
        'All exam boards covered',
        'Customized past papers',
        'Direct examiner insights',
        'Flexible scheduling',
        'Block booking discounts available'
      ],
      popular: false,
      premium: true,
      buttonText: "Book Consultation",
      note: "Block booking discounts available"
    },
    {
      name: "VIP 1:1 Maths + Science",
      price: "99.99",
      period: "per hour",
      description: "Premium one-on-one tuition for Maths AND Sciences - maximum progress",
      icon: Users,
      features: [
        '1 hour private session',
        'Exclusive 1-on-1 attention',
        'GCSE/IGCSE Maths',
        'GCSE/IGCSE Chemistry',
        'GCSE/IGCSE Physics',
        'GCSE/IGCSE Biology',
        'Fully personalized plan',
        'All exam boards covered',
        'Customized past papers',
        'Direct examiner insights',
        'Block booking discounts'
      ],
      popular: false,
      premium: true,
      buttonText: "Book Consultation"
    }
  ];

  const additionalInfo = [
    { icon: Users, text: 'Small Groups (4-6 students)' },
    { icon: Clock, text: '1 Hour Sessions' },
    { icon: Calendar, text: 'Flexible Scheduling' }
  ];

  return (
    <section id="pricing" className="parallax-section" style={{ 
      padding: '6rem 1.5rem', 
      backgroundImage: `linear-gradient(rgba(255, 252, 245, 0.97), rgba(248, 245, 237, 0.97)), url('https://static.prod-images.emergentagent.com/jobs/ee1a1db2-a588-42e0-b44d-7c9f207c1d4b/images/35b31ee724e9030f3b7858ea11af80ad84cfd6da00ab1f7b2563d00a2fac30c4.png')`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed'
    }}>
      <div className="container">
        {/* Animated Header */}
        <div 
          ref={headerRef}
          className={`fade-up ${headerVisible ? 'visible' : ''}`}
          style={{ textAlign: 'center', marginBottom: '3rem' }}
        >
          <h2 className="heading-2" style={{ marginBottom: '1rem', color: 'white' }}>
            Simple, Transparent Pricing
          </h2>
          <p className="body-large" style={{ color: 'rgba(255, 255, 255, 0.8)', maxWidth: '700px', margin: '0 auto' }}>
            Choose the option that works best for you - flexible or committed
          </p>
        </div>

        {/* Animated Pricing Cards Grid */}
        <div 
          ref={cardsRef}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '2rem',
            maxWidth: '1000px',
            margin: '0 auto 3rem'
          }}
        >
          {pricingOptions.map((option, index) => {
            const IconComponent = option.icon;
            return (
              <div 
                key={index} 
                className={`product-card ${cardsVisible ? 'visible' : ''}`}
                style={{
                  textAlign: 'center',
                  border: option.popular ? '2px solid var(--accent-primary)' : 
                          option.premium ? '2px solid var(--accent-secondary)' : '1px solid var(--border-light)',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  opacity: cardsVisible ? 1 : 0,
                  transform: cardsVisible ? 'translateY(0)' : 'translateY(40px)',
                  transition: 'all 0.7s cubic-bezier(0.4, 0, 0.2, 1)',
                  transitionDelay: `${index * 0.2}s`
                }}
              >
                {/* Popular Badge */}
                {option.popular && (
                  <div style={{
                    position: 'absolute',
                    top: '-12px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: '#C5A059',
                    color: '#0F172A',
                    padding: '4px 16px',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    letterSpacing: '0.5px'
                  }}>
                    Most Popular
                  </div>
                )}

                {/* Premium Badge */}
                {option.premium && (
                  <div style={{
                    position: 'absolute',
                    top: '-12px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: '#0F172A',
                    color: '#C5A059',
                    padding: '4px 16px',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    letterSpacing: '0.5px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <Crown size={12} />
                    Premium
                  </div>
                )}

                {/* Icon */}
                <div style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '12px',
                  background: 'var(--accent-wash)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1.5rem'
                }}>
                  <IconComponent size={28} color="var(--accent-text)" />
                </div>

                {/* Title */}
                <h3 className="heading-3" style={{ marginBottom: '0.5rem' }}>
                  {option.name}
                </h3>

                {/* Price */}
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '0.5rem' }}>
                    <span style={{ fontSize: '2.5rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                      £{option.price}
                    </span>
                  </div>
                  <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                    {option.period}
                  </p>
                  {option.savings && (
                    <div style={{
                      marginTop: '0.5rem',
                      padding: '0.25rem 0.75rem',
                      background: 'var(--accent-secondary)',
                      color: 'white',
                      borderRadius: '9999px',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      display: 'inline-block'
                    }}>
                      {option.savings}
                    </div>
                  )}
                </div>

                {/* Description */}
                <p className="body-small" style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
                  {option.description}
                </p>

                {/* Features List */}
                <div style={{ marginBottom: '2rem', textAlign: 'left', flex: 1 }}>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {option.features.map((feature, idx) => (
                      <li key={idx} style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.75rem', alignItems: 'center' }}>
                        <div style={{
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          background: 'var(--accent-wash)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          <Check size={14} color="var(--accent-text)" strokeWidth={3} />
                        </div>
                        <span className="body-small">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA Button */}
                <button 
                  onClick={scrollToContact} 
                  className={option.popular ? "btn-primary" : option.premium ? "btn-primary" : "btn-secondary"}
                  style={{ 
                    width: '100%', 
                    marginTop: 'auto',
                    ...(option.premium && {
                      background: 'linear-gradient(to bottom right, var(--accent-secondary), var(--accent-secondary-strong))'
                    })
                  }}
                >
                  {option.buttonText}
                </button>
              </div>
            );
          })}
        </div>

        {/* Additional Info */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '1rem',
          maxWidth: '700px',
          margin: '0 auto'
        }}>
          {additionalInfo.map((item, index) => {
            const IconComponent = item.icon;
            return (
              <div key={index} style={{
                background: 'var(--bg-card)',
                border: '1px solid var(--border-light)',
                borderRadius: '12px',
                padding: '1rem',
                textAlign: 'center'
              }}>
                <IconComponent size={24} color="var(--accent-text)" style={{ margin: '0 auto 0.5rem' }} />
                <p className="body-small" style={{ margin: 0 }}>{item.text}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
